// Test Status Label
// ------------------
// Single source of truth for the user-facing label + color treatment
// of a test's outcome. Used by every surface that shows test status —
// the Dashboard per-app card pill, the Applications page row pill,
// the WebApplication page TestDetails badge, and each entry's suffix
// in the TestSelector dropdown.
//
// Logic mirrors the BE's setStatus phase computation (see
// FailedTestFindings_BEBrief.md): a test is "failed" when its
// testStatus is Failed/Invalid *or* its endTrigger is one of the
// incomplete-stop values (Cancelled / InfrastructureFailure /
// ServerUnreachable). Without this alignment, a test that the BE
// flagged Test_Failed (because endTrigger=Cancelled even though
// testStatus=Completed) would render "Test Failed" on the Dashboard
// but "Completed" on the webapp page — the inconsistency the user
// caught on AlphaInvest.
//
// The "Completed" outcome doesn't try to encode findings count
// (Results Ready vs No Findings) — that info lives in the severity
// tiles / per-app pills next to the status pill. Keeping the status
// pill answer to one question ("did this run finish cleanly?")
// keeps it consistent across every surface.

import {
  TEST_STATUS_COMPLETED,
  TEST_STATUS_FAILED,
  TEST_STATUS_INVALID,
} from "@/constants/definitions";

// endTrigger values that indicate a test did NOT complete its
// intended assessment. The BE flags these as Test_Failed when
// computing webAppTestStatus.currentPhase; the FE mirrors that for
// every status / pill surface AND for the dashboard's
// hasAppBeenTested filter. Centralised here as the single source of
// "an endTrigger that doesn't represent a clean run".
export const INCOMPLETE_END_TRIGGERS: ReadonlySet<string> = new Set([
  "Cancelled",
  "InfrastructureFailure",
  "ServerUnreachable",
  "DomainVerificationFailed",
]);

const IN_FLIGHT_STATUSES = new Set([
  "Provisioning",
  "Running",
  "Stopping",
]);

// endTrigger value emitted by the BE when the test stopped because
// the user (or another admin) clicked Stop Test mid-run, distinct
// from the technical-failure end triggers above. The pill surfaces
// this as "Stopped Early" so the user-initiated halt reads
// differently from a clean completion or an infrastructure failure.
const END_TRIGGER_MANUAL_STOP = "ManualStop";

export type TestResultTone = "success" | "danger" | "info" | "neutral";

// label    — long form used by status pills on Dashboard / Applications
//             / Security Status (e.g. "Stopped Early").
// shortLabel — compact form used by the TestSelector dropdown where
//             every option is prefixed by the date and the column is
//             narrow (e.g. "Stopped").
// tone     — drives the color treatment via STATUS_TONE_STYLES.
export interface TestResultLabel {
  label: string;
  shortLabel: string;
  tone: TestResultTone;
}

export function getTestResultLabel(input: {
  testStatus?: string | null;
  endTrigger?: string | null;
}): TestResultLabel {
  const status = input.testStatus ?? "";
  const trigger = input.endTrigger ?? "";

  if (IN_FLIGHT_STATUSES.has(status)) {
    return { label: "Live", shortLabel: "Live", tone: "info" };
  }

  // testStatus Failed / Invalid (BE failure) and the
  // INCOMPLETE_END_TRIGGERS (Cancelled / InfrastructureFailure /
  // ServerUnreachable) take precedence over ManualStop — a Failed
  // test that was also manually stopped reads as Failed because the
  // failure is the more actionable signal.
  if (
    status === TEST_STATUS_FAILED ||
    status === TEST_STATUS_INVALID ||
    INCOMPLETE_END_TRIGGERS.has(trigger)
  ) {
    return { label: "Test Failed", shortLabel: "Failed", tone: "danger" };
  }

  // ManualStop sits between failure and clean completion. The user
  // halted the run before it finished its assessment — nothing went
  // wrong, but the result isn't a complete picture either. Neutral
  // tone so it doesn't read as success-or-danger.
  if (trigger === END_TRIGGER_MANUAL_STOP) {
    return { label: "Stopped Early", shortLabel: "Stopped", tone: "neutral" };
  }

  if (status === TEST_STATUS_COMPLETED) {
    return { label: "Completed", shortLabel: "Completed", tone: "success" };
  }

  return { label: "Unknown", shortLabel: "Unknown", tone: "neutral" };
}

// Shared tone → Tailwind class triplet. One definition, every surface
// reads from it. dotColor is used by surfaces that pair the label
// with a small status dot (Dashboard / Applications pills); bgColor
// + textColor wrap the pill text.
export const STATUS_TONE_STYLES: Record<
  TestResultTone,
  { bgColor: string; textColor: string; dotColor: string; ringColor: string }
> = {
  success: {
    bgColor: "bg-emerald-50",
    textColor: "text-emerald-700",
    dotColor: "bg-emerald-500",
    ringColor: "ring-emerald-600/20",
  },
  danger: {
    bgColor: "bg-red-50",
    textColor: "text-red-700",
    dotColor: "bg-red-500",
    ringColor: "ring-red-600/20",
  },
  info: {
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
    dotColor: "bg-blue-500",
    ringColor: "ring-blue-600/20",
  },
  neutral: {
    bgColor: "bg-gray-100",
    textColor: "text-gray-600",
    dotColor: "bg-gray-400",
    ringColor: "ring-gray-400/20",
  },
};
